/**
 * PTL Live Earnings Ticker — pulls /api/ptl/live-ticker every 12s and
 * scrolls the most recent earning events horizontally. Pure social
 * proof; no auth required, no PII (usernames are masked server-side).
 *
 * Usage:
 *   <EarningsTicker compact />          ← tight one-line variant
 *   <EarningsTicker />                  ← full marquee with avatars
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Music, Heart, MessageCircle, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 12_000;

const KIND_ICON = {
  play: Music,
  like_bonus: Heart,
  comment_bonus: MessageCircle,
};
const KIND_LABEL = {
  play: 'earned',
  like_bonus: 'liked',
  comment_bonus: 'commented on',
};
const KIND_COLOR = {
  play: '#00ff88',
  like_bonus: '#FF66AA',
  comment_bonus: '#A855F7',
};

export function EarningsTicker({ compact = false, className = '' }) {
  const [items, setItems] = useState([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchTick = async () => {
      try {
        const r = await axios.get(`${API}/ptl/live-ticker?limit=8`);
        if (alive) setItems(r.data?.items || []);
      } catch {
        // silent — ticker is decorative
      }
    };
    fetchTick();
    intervalRef.current = setInterval(fetchTick, REFRESH_MS);
    return () => {
      alive = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (!items.length) {
    // Subtle placeholder so the slot doesn't pop in/out as data arrives
    return (
      <div
        className={`text-[#666] font-mono text-xs italic ${className}`}
        data-testid="earnings-ticker-empty"
      >
        💸 Live listener earnings — be the first to bank a payout this hour.
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      data-testid="earnings-ticker"
      aria-live="polite"
    >
      <style>{`
        @keyframes ptl-ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      <div
        className="flex gap-6 whitespace-nowrap"
        style={{
          animation: `ptl-ticker-scroll ${Math.max(28, items.length * 5)}s linear infinite`,
          willChange: 'transform',
        }}
      >
        {/* Duplicate the list for a seamless loop */}
        {[...items, ...items].map((item, i) => {
          const Icon = KIND_ICON[item.kind] || DollarSign;
          const color = KIND_COLOR[item.kind] || '#FFDA33';
          return (
            <span
              key={i}
              className={`inline-flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'} font-mono`}
            >
              <Icon size={compact ? 11 : 13} style={{ color }} />
              <span className="text-white font-semibold" style={{ color }}>
                @{item.username_masked}
              </span>
              <span className="text-[#aaa]">{KIND_LABEL[item.kind]}</span>
              {item.title && (
                <span className="text-white truncate max-w-[180px] inline-block align-middle">
                  "{item.title}"
                </span>
              )}
              <span className="text-[#00ff88] font-bold">
                +${item.amount.toFixed(3)}
              </span>
              <span className="text-[#666] mx-2">·</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default EarningsTicker;
